import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, deviceFingerprint, deviceName, errMsg } from "@/lib/api";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

export function AppProvider({ children }) {
  const [user, setUser] = useState(false);
  const [license, setLicense] = useState({ activated: false });
  const [facilities, setFacilities] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [facilityId, setFacilityId] = useState(() => localStorage.getItem("mc_facility") || "all");
  const [theme, setTheme] = useState(() => localStorage.getItem("mc_theme") || "light");

  const refreshLicense = useCallback(async () => {
    const { data } = await api.get(`/license/status?fingerprint=${deviceFingerprint()}`);
    setLicense(data);
    return data;
  }, []);

  useEffect(() => {
    (async () => {
      const licenseData = await refreshLicense().catch(() => ({
        activated: false,
      }));

      if (!licenseData?.activated) {
        setUser(false);
        return;
      }

      if (!sessionStorage.getItem("mc_token")) {
        setUser(false);
        return;
      }

      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, [refreshLicense]);

  const refreshFacilities = useCallback(async () => {
    const { data } = await api.get("/facilities");
    setFacilities(data);
    return data;
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshFacilities().catch(() => {});
    api.get("/auth/permissions").then(({ data }) => setPermissions(data.permissions || {})).catch(() => {});
  }, [user, refreshFacilities]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", {
      username, password,
      device_fingerprint: deviceFingerprint(),
      device_name: deviceName(),
    });
    sessionStorage.setItem("mc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const activate = async (key) => {
    await api.post("/license/activate", {
      key, device_fingerprint: deviceFingerprint(), device_name: deviceName(),
    });
    await refreshLicense();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    sessionStorage.removeItem("mc_token");
    setUser(false);
  };

  const selectFacility = (id) => {
    setFacilityId(id);
    localStorage.setItem("mc_facility", id);
  };

  const fq = facilityId === "all" ? "" : `facility_id=${facilityId}`;

  return (
    <AppCtx.Provider value={{
      user, license, facilities, facilityId, selectFacility, fq, theme, setTheme,
      login, logout, activate, refreshLicense, refreshFacilities, permissions, errMsg,
    }}>
      {children}
    </AppCtx.Provider>
  );
}
